import { useEffect, useState } from 'react';

import {
  ChakraFlex,
  ChakraSpacer,
  FAIconName,
  FireflyButton,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';

import { PatientEvents } from '~/analytics/patientEvents';
import { ApiCommand } from '~/lib/Api';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { VITE_OLD_INTAKE_SURVEY_IDS } from '~/lib/constants/env';
import {
  patientSurveyMapping,
  patientSurveyMappingValues,
} from '~/lib/constants/patients.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import urlConstants from '~/lib/constants/url.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useApi } from '~/lib/hooks';
import { mapSurvey, stringToJson } from '~/lib/util/survey.util';

import EditBasicInfoForm from './EditBasicInfoForm';
import ViewBasicInfo from './ViewBasicInfo';

const { getPatientById: getPatientByIdUrl } = urlConstants.patients;

const TOP_LEVEL_ID = 'patient-profile__basic-info';

interface BasicInfoProps {
  assessmentList: SurveyResponse[];
  patientId: string;
  hasPatientDashboardPermission: boolean;
  patientBasicDetails: Patient;
  setPatientBasicDetails: (data: Patient) => void;
}

const BasicInfo = ({
  assessmentList,
  patientId,
  hasPatientDashboardPermission,
  patientBasicDetails,
  setPatientBasicDetails,
}: BasicInfoProps) => {
  const [isBasicInfoEditMode, setisBasicInfoEditMode] =
    useState<boolean>(false);
  const [appendSurveyResult, setAppendSurveyResult] = useState<any>({});
  const [isDisplayInformationBox, setIsDisplayInformationBox] =
    useState<boolean>(false);

  // TODO This could use the grouped assessments API instead
  const existingSurvey = (assessmentList ?? []).find(
    ({ status, surveyName: _surveyName }: SurveyResponse) =>
      _surveyName === SurveyName.ADMISSIONS_INTAKE &&
      status !== SurveyResponseStatus.VOID,
  );
  const surveyJson = stringToJson(existingSurvey?.jsonResults ?? '{}');
  const isV11IntakeSurvey = !VITE_OLD_INTAKE_SURVEY_IDS?.split(',').includes(
    existingSurvey?.surveyExternalId,
  );

  const { trackEvent } = useAnalytics();

  const {
    error: getPatientBasicInfoError,
    data: getPatientBasicInfoResponse,
    loading: isLoading,
    sendRequest: getPatientBasicInfo,
    statusCode: getPatientBasicInfoStatusCode,
  } = useApi<Patient>();

  useEffect(() => {
    getPatientBasicInfo({
      command: ApiCommand.GET,
      url: getPatientByIdUrl(patientId),
    });
  }, [patientId]);

  useEffect(() => {
    if (getPatientBasicInfoStatusCode === 200) {
      const appendSurveyResult: AppendSurveyMappingItems = isV11IntakeSurvey
        ? mapSurvey(
            surveyJson,
            getPatientBasicInfoResponse,
            patientSurveyMapping,
            patientSurveyMappingValues,
          )
        : {};

      setAppendSurveyResult(appendSurveyResult);
      setPatientBasicDetails({
        ...getPatientBasicInfoResponse,
        ...appendSurveyResult,
      });
      if (Object.keys(appendSurveyResult).length > 0) {
        const updatedSurveyResult = { ...appendSurveyResult };
        if (Object.keys(updatedSurveyResult).length > 0) {
          setIsDisplayInformationBox(true);
        }
      }
    } else if (getPatientBasicInfoError) {
      console.error(
        'Error getting patient basic info',
        getPatientBasicInfoError,
      );
    }
  }, [getPatientBasicInfoError, getPatientBasicInfoStatusCode]);

  const handleOnUpdate = () => {
    getPatientBasicInfo({
      command: ApiCommand.GET,
      url: getPatientByIdUrl(patientId),
    });
    setisBasicInfoEditMode(false);
  };

  const BasicInfoViewOrEdit =
    patientBasicDetails &&
    (hasPatientDashboardPermission && isBasicInfoEditMode ? (
      <EditBasicInfoForm
        appendSurveyResult={appendSurveyResult}
        basicInfo={patientBasicDetails}
        onDiscard={() => setisBasicInfoEditMode(false)}
        onUpdate={handleOnUpdate}
        patientId={patientId}
      />
    ) : (
      <ViewBasicInfo
        basicInfo={patientBasicDetails}
        displayInformationBox={isDisplayInformationBox}
        onSurveyButtonClick={() => {
          trackEvent(PatientEvents.editPatientProfileTabButtonClicked, {
            Source: AnalyticsSourceValues.PATIENT_TAB,
          });
          setisBasicInfoEditMode(true);
        }}
      />
    ));
  const handleEditClick = () => {
    trackEvent(PatientEvents.editPatientProfileTabButtonClicked, {
      Source: AnalyticsSourceValues.PATIENT_TAB,
    });
    setisBasicInfoEditMode(true);
  };

  const shouldShowEditButton =
    hasPatientDashboardPermission && !isBasicInfoEditMode;

  return (
    <>
      <ChakraFlex marginBottom="18px">
        <Text id={`${TOP_LEVEL_ID}__header`} variant="h5">
          Basic info
        </Text>
        <ChakraSpacer />

        {shouldShowEditButton && (
          <FireflyButton
            ariaLabel="basic info edit button"
            id={`${TOP_LEVEL_ID}__edit`}
            leftIcon={FAIconName.penRegular}
            onClick={handleEditClick}
            size="small"
            variant="secondary"
          >
            Edit
          </FireflyButton>
        )}
      </ChakraFlex>

      <SkeletonLoader
        height={isLoading ? '600px' : 'auto'}
        id={TOP_LEVEL_ID}
        isLoaded={!isLoading}
      >
        {BasicInfoViewOrEdit}
      </SkeletonLoader>
    </>
  );
};

export default BasicInfo;
